import type { CSSProperties, JSX } from 'react';

/**
 * Sigma — the Yuan Math mascot.
 *
 * A guide rather than a decoration: it is the thing that reacts when an answer
 * lands, so a child gets a face back instead of a colour change. Drawn as
 * inline SVG so it scales to any size, inherits the theme, prints, and ships
 * no sprite sheet; animated by class so it needs no animation library and
 * honours `prefers-reduced-motion` through the global rule in `theme.css`.
 *
 * The moods are deliberately few. `sad` is a *sympathetic* face, never a
 * scolding one — a wrong answer here is information, and the mascot's job on
 * that frame is to stay in the room.
 */
export type MascotMood = 'idle' | 'happy' | 'sad' | 'thinking' | 'celebrate';

export function Mascot({
  mood = 'idle',
  size = 88,
  className = '',
}: {
  mood?: MascotMood;
  size?: number;
  className?: string;
}): JSX.Element {
  // A blink is a squashed eye; a smile curves the closed ones upward instead.
  const smiling = mood === 'happy' || mood === 'celebrate';
  const eyeHeight = smiling ? 4 : mood === 'sad' ? 7 : 9;

  return (
    <div
      className={`mascot mascot--${mood} ${className}`}
      style={{ width: size, height: size, ['--mascot-size' as keyof CSSProperties]: `${size}px` } as CSSProperties}
      aria-hidden="true"
    >
      <svg viewBox="0 0 100 100" width={size} height={size} role="presentation" focusable="false">
        {/* antenna — the bulb changes colour with the mood, which is the
            fastest read at thumbnail size */}
        <line x1="50" y1="20" x2="50" y2="9" stroke="var(--accent-edge)" strokeWidth="4" strokeLinecap="round" />
        <circle
          className="mascot__bulb"
          cx="50"
          cy="7"
          r="5.5"
          fill={mood === 'sad' ? 'var(--berry-face)' : 'var(--gold-face)'}
        />

        {/* body */}
        <rect x="15" y="20" width="70" height="60" rx="22" fill="var(--accent-face)" />
        <rect x="15" y="20" width="70" height="60" rx="22" fill="none" stroke="var(--accent-edge)" strokeWidth="3" />

        {/* face plate — a small slate, because this one teaches mathematics */}
        <rect x="25" y="31" width="50" height="35" rx="15" fill="#2b3040" />

        {/* eyes */}
        {smiling ? (
          <>
            <path d="M33 47 q6 -8 12 0" stroke="var(--accent-face)" strokeWidth="4" fill="none" strokeLinecap="round" />
            <path d="M55 47 q6 -8 12 0" stroke="var(--accent-face)" strokeWidth="4" fill="none" strokeLinecap="round" />
          </>
        ) : (
          <>
            <rect x="35" y={44 - eyeHeight / 2} width="8" height={eyeHeight} rx="4" fill="var(--accent-face)" />
            <rect x="57" y={44 - eyeHeight / 2} width="8" height={eyeHeight} rx="4" fill="var(--accent-face)" />
          </>
        )}

        {/* mouth */}
        {mood === 'sad' ? (
          <path d="M40 60 q10 -7 20 0" stroke="var(--accent-face)" strokeWidth="3.5" fill="none" strokeLinecap="round" />
        ) : (
          <path d="M40 55 q10 8 20 0" stroke="var(--accent-face)" strokeWidth="3.5" fill="none" strokeLinecap="round" />
        )}

        {/* arms and feet */}
        <rect x="5" y="43" width="10" height="22" rx="5" fill="var(--accent-edge)" />
        <rect x="85" y="43" width="10" height="22" rx="5" fill="var(--accent-edge)" />
        <rect x="27" y="78" width="17" height="10" rx="5" fill="var(--accent-edge)" />
        <rect x="56" y="78" width="17" height="10" rx="5" fill="var(--accent-edge)" />

        {/* the wordmark, worn as a badge */}
        <text
          className="mascot__sigma"
          x="50"
          y="76"
          textAnchor="middle"
          fill="var(--accent-edge)"
          fontSize="13"
          fontWeight="800"
        >
          ∑
        </text>
      </svg>
    </div>
  );
}
